import { useRef, useCallback, useEffect } from 'react';

/**
 * Magnetic button — follows cursor on hover with elastic physics. Zero re-renders.
 */
export function MagBtn({ children, className, onClick, onMouseEnter, onMouseLeave, style = {}, ...rest }) {
  const ref = useRef(null);

  const handleMove = useCallback((e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const x = (e.clientX - r.left - r.width / 2) * 0.3;
    const y = (e.clientY - r.top - r.height / 2) * 0.3;
    ref.current.style.transform = `translate(${x}px, ${y}px)`;
  }, []);

  const handleLeave = useCallback(
    (e) => {
      if (ref.current) ref.current.style.transform = 'translate(0,0)';
      if (onMouseLeave) onMouseLeave(e);
    },
    [onMouseLeave]
  );

  const handleEnter = useCallback(
    (e) => {
      if (onMouseEnter) onMouseEnter(e);
    },
    [onMouseEnter]
  );

  return (
    <button
      ref={ref}
      className={className}
      onClick={onClick}
      onMouseMove={handleMove}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      {...rest}
      style={{
        ...style,
        transition: 'transform .3s cubic-bezier(.23,1,.32,1)',
      }}
    >
      {children}
    </button>
  );
}

/**
 * 3D tilt card — rotates based on mouse position with perspective. Zero re-renders.
 */
export function TiltCard({ children, className }) {
  const ref = useRef(null);

  const handleMove = useCallback((e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    ref.current.style.transform = `perspective(800px) rotateX(${(y - 0.5) * -12}deg) rotateY(${(x - 0.5) * 12}deg) scale(1.02)`;
    ref.current.style.boxShadow = `${-(x - 0.5) * 24}px ${(y - 0.5) * 24}px 40px rgba(0,0,0,.4)`;
  }, []);

  const handleLeave = useCallback(() => {
    if (ref.current) { ref.current.style.transform = ''; ref.current.style.boxShadow = ''; }
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      style={{
        transition: 'all .5s cubic-bezier(.23,1,.32,1)',
        transformStyle: 'preserve-3d',
      }}
    >
      {children}
    </div>
  );
}

/**
 * Animated counter — counts from 0 to target when visible. Uses RAF for smoothness.
 */
export function Counter({ target, suffix = '', visible }) {
  const ref = useRef(null);
  const animated = useRef(false);

  useEffect(() => {
    if (!visible || animated.current) return;
    animated.current = true;
    const duration = 2000;
    const start = performance.now();
    const tick = now => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      if (ref.current) ref.current.textContent = Math.round(eased * target) + suffix;
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [visible, target, suffix]);

  return <span ref={ref}>0{suffix}</span>;
}
